import { Box, Button, Card, CardContent, Grid, Stack, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import Chart from "react-google-charts";
import Maps from "../Maps/Maps";
import { BusinessCenter, People } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListCheck } from '@fortawesome/free-solid-svg-icons';
import { options, optionsPieChart } from "../optionsChart";
import {
  getClientCount,
  getClientsPieData,
  getJobsWeeklyStatistics,
  getProjectCount,
  getUserCount,
} from "../../../../api/apiCall";
import { useAuth } from "../../../../auth/auth";
import { useNavigate } from "react-router-dom";
import { format, startOfWeek } from 'date-fns';
import styles from './Dashboard.module.css';

const AdminDashboard = () => {
    const {state} = useAuth();
    const navigate = useNavigate();
    const [userCount, setUserCount] = useState(0);
    const [clientCount, setClientCount] = useState(0);
    const [projectCount, setProjectCount] = useState(0);
    const [clientPieData, setClientPieData] = useState([]);
    const [jobStatistics, setJobStatistics] = useState<any>(undefined);
    const [week, setWeek] = useState(1);
    const [year, setYear] = useState(2025);
    const [weekInput, setWeekInput] = useState(1);
    const [yearInput, setYearInput] = useState(2025);
    const [startOfTheWeek, setStartOfTheWeek] = useState<any>();
    const [endOfTheWeek, setEndOfTheWeek] = useState<any>();
    const [, setDate] = useState<any>(undefined);

    const getRailwayYearStartDate = (year: number): Date => {
      const aprilFirst = new Date(year, 3, 1);
      const dayOfWeek = aprilFirst.getDay();
      const daysBeforeSaturday = (dayOfWeek + 1) % 7;
      return new Date(year, 2, 31 - daysBeforeSaturday);
    };

    const getWeekStartDate = (week: number, year: number): Date => {
        const railwayYearStartDate = getRailwayYearStartDate(year);
        const startDate = new Date(railwayYearStartDate);
        startDate.setDate(railwayYearStartDate.getDate() + week * 7);
        setDate(startDate);
        getWeekStartingFromSaturday(startDate);
        return startDate;
    };

    const getClientsPieDatas = async () => {
        try {
            const response = await getClientsPieData(state.user?.token, week, year)
            setClientPieData(response?.data || [])
        } catch (error) {
            console.log(error)
        }
    }

    const getJobsStatistics = async () => {
        try {
            const response = await getJobsWeeklyStatistics(state.user?.token, week, year)
            setJobStatistics(response);
        } catch (error) {
            console.log(error)
      }
    }

    const getWeekStartingFromSaturday = (date: Date) => {
        const saturday = startOfWeek(date, { weekStartsOn: 6 });
        const friday = startOfWeek(date, { weekStartsOn: 5 });
        setStartOfTheWeek(saturday);
        setEndOfTheWeek(friday)
    };

    useEffect(() => {
        getJobsStatistics();
        getClientsPieDatas();
        getWeekStartDate(week, year);
    }, [week, year]);

    const days = ['SAT', 'SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI'];
    const data = [
      ["Day", "Shifts", { role: "annotation" }],
      ...days.map((day, i) => [
        day,
        jobStatistics ? jobStatistics[i] : undefined,
        jobStatistics ? jobStatistics[i] : undefined,
      ]),
    ];

    const pieData: any = clientPieData.map(({ name, value }) => [name, value]);
    pieData.unshift(["Clients", "Shifts"]);

    const stats = [
        {
          value: userCount,
          label: "Users",
          icon: <People sx={{color: "#06b6d4", fontSize: 40}} />,
          url: '/pace-team/resourcing/staff-details',
        },
        {
          value: clientCount,
          label: "Clients",
          icon: <BusinessCenter sx={{color: "#8b5cf6", fontSize: 40}} />,
          url: '/pace-team/collaborations/clients',
        },
        {
          value: projectCount,
          label: "Projects",
          icon: <FontAwesomeIcon icon={faListCheck} style={{color: "#f59e0b", fontSize: 33}} />,
          url: '/pace-team/collaborations/projects',
        },
    ];

    const getUserCounts = async () => {
        const response = await getUserCount(state.user?.token);
        setUserCount(response)
    }

    const getClientCounts = async () => {
        const response = await getClientCount(state.user?.token);
        setClientCount(response)
    }

    const getProjectCounts = async () => {
        const response = await getProjectCount(state.user?.token);
        setProjectCount(response)
    }

    useEffect(() => {
        getUserCounts();
        getClientCounts();
        getProjectCounts();
    }, []);

    const handleNavigate = async (url: any) => {
        navigate(url);
    };

    return (
        <Box width={"100%"}>
            <Box p={2}>
                <Typography variant='h5' fontWeight="bold" mb={2}>Dashboard</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4.8}>
                        <Card variant="outlined" sx={{borderRadius: 3}}>
                            <CardContent>
                                <Typography color={"#5470c6"}>
                                    Week {week} / Saturday:{" "}
                                    {format(startOfTheWeek ? startOfTheWeek : 0, "dd/MM/yyyy")}{" "}
                                    - Friday:{" "}
                                    {format(endOfTheWeek ? endOfTheWeek : 0, "dd/MM/yyyy")}
                                </Typography>
                                <Box display={"flex"} justifyContent={"space-evenly"} mt={2.7} gap={1.5}>
                                    <TextField
                                        label="Week"
                                        size="small"
                                        type="number"
                                        value={weekInput}
                                        onChange={(e: any) => setWeekInput(e.target.value)} />
                                    <TextField
                                        label="Year"
                                        size="small"
                                        type="number"
                                        value={yearInput}
                                        onChange={(e: any) => setYearInput(e.target.value)} />
                                    <Button
                                        variant="contained"
                                        style={{boxShadow: "none"}}
                                        onClick={() => {setWeek(weekInput); setYear(yearInput)}}>
                                            Filter
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    {stats.map((stat) => (
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Card
                                variant="outlined"
                                className={styles.statsCard}
                                sx={{borderRadius: 3}}
                                onClick={() => handleNavigate(stat.url)}
                            >
                                <CardContent>
                                    <Stack direction="row" spacing={2} alignItems="center" >
                                        {stat.icon}
                                        <Box>
                                            <Typography variant="h6" fontWeight="bold">
                                                {stat.value}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {stat.label}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
            <Box display={"flex"}>
                <Card variant="outlined" sx={{borderRadius: 3}} className={styles.columnChartCard}>
                    <h4 className={styles.h4Text}>Shifts Report</h4>
                    <p className={styles.p4Text}>Week {week}</p>
                    <Chart
                        chartType="ColumnChart"
                        width="100%"
                        height="400px"
                        data={data}
                        options={options}
                    />
                </Card>
                <Card variant="outlined" sx={{borderRadius: 3}} className={styles.pieChartCard}>
                    <h4 className={styles.h4Text}>Shifts by Client Report</h4>
                    <p className={styles.p4Text}>Week {week}</p>
                    <Chart
                        chartType="PieChart"
                        width="100%"
                        height="500px"
                        data={pieData}
                        options={optionsPieChart}
                    />
                </Card>
            </Box>
            <Box m={2}>
                <Card variant="outlined" className={styles.mapsContainer}>
                    <h4 className={styles.h4Text}></h4>
                    <Maps />
                </Card>
            </Box>
        </Box>
    )
}

export default AdminDashboard;
